import type { ReactNode } from 'react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import type { CoreEnumDTO } from 'src/api/core/core-enums/useCoreEnums';

export interface InlineCategory {
  name: string;
  description: string;
}

export function ClassificationCategoryFields({
  classificationSource,
  coreEnumId,
  categories,
  enums,
  selectedEnum,
  onClassificationSourceChange,
  onCoreEnumIdChange,
  onCategoriesChange,
  enumHelp,
  inlineHelp,
}: {
  classificationSource: string;
  coreEnumId: string;
  categories: InlineCategory[];
  enums: CoreEnumDTO[];
  selectedEnum: CoreEnumDTO | undefined;
  onClassificationSourceChange: (source: string) => void;
  onCoreEnumIdChange: (id: string) => void;
  onCategoriesChange: (categories: InlineCategory[]) => void;
  enumHelp?: ReactNode;
  inlineHelp?: ReactNode;
}) {
  const gt = useGT();

  const updateCategory = (
    index: number,
    field: keyof InlineCategory,
    value: string,
  ) => {
    const updated = [...categories];
    const current = updated[index];
    if (current === undefined) return;
    updated[index] = { ...current, [field]: value };
    onCategoriesChange(updated);
  };

  return (
    <>
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <T>Classification Source</T>{' '}
          <span className="text-destructive">*</span>
        </Label>
        <Select
          value={classificationSource}
          onValueChange={onClassificationSourceChange}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inline">
              <T>Inline Categories</T>
            </SelectItem>
            <SelectItem value="enum">
              <T>Core Enum</T>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {classificationSource === 'enum' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            <T>Core Enum</T> <span className="text-destructive">*</span>
          </Label>
          <Select value={coreEnumId} onValueChange={onCoreEnumIdChange}>
            <SelectTrigger
              className={!coreEnumId ? 'border-destructive' : undefined}
            >
              <SelectValue
                placeholder={gt('Select a Core Enum...', {
                  context: 'core enum select placeholder',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {enums.map((entry) => (
                <SelectItem key={entry.id} value={entry.id}>
                  {entry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!coreEnumId && (
            <p className="text-xs text-destructive">
              <T>A Core Enum must be selected for this step to run.</T>
            </p>
          )}
          {enumHelp}
          {selectedEnum?.options && selectedEnum.options.length > 0 && (
            <div className="rounded-md border p-2 space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                <T context="count of enum categories">
                  Categories (<Var>{selectedEnum.options.length}</Var>)
                </T>
              </p>
              <TooltipProvider>
                <div className="flex flex-wrap gap-1">
                  {selectedEnum.options.map((opt) => (
                    <Tooltip key={opt.id}>
                      <TooltipTrigger asChild>
                        <div>
                          <Badge
                            variant="outline"
                            className="text-[10px] cursor-default"
                          >
                            {opt.name}
                          </Badge>
                        </div>
                      </TooltipTrigger>
                      {opt.description && (
                        <TooltipContent>
                          <p className="max-w-xs text-xs">{opt.description}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  ))}
                </div>
              </TooltipProvider>
            </div>
          )}
        </div>
      )}

      {classificationSource === 'inline' && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            <T>Categories</T> <span className="text-destructive">*</span>
          </Label>
          {categories.map((cat, index) => (
            // Category rows are an editable list without stable ids.
            // react-doctor-disable-next-line react-doctor/no-array-index-as-key
            <div key={index} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  <T context="category number label">
                    Category <Var>{index + 1}</Var>
                  </T>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onCategoriesChange(categories.filter((_, i) => i !== index))
                  }
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Input
                placeholder={gt('Category name', {
                  context: 'classification category name input placeholder',
                })}
                value={cat.name}
                onChange={(e) => updateCategory(index, 'name', e.target.value)}
              />
              <Input
                placeholder={gt('Description (helps AI classify accurately)', {
                  context:
                    'classification category description input placeholder',
                })}
                value={cat.description}
                onChange={(e) =>
                  updateCategory(index, 'description', e.target.value)
                }
              />
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onCategoriesChange([...categories, { name: '', description: '' }])
            }
            className="w-full"
          >
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            <T>Add Category</T>
          </Button>
          {categories.length === 0 && (
            <p className="text-xs text-destructive">
              <T>At least one category is required.</T>
            </p>
          )}
          {inlineHelp}
        </div>
      )}
    </>
  );
}
